// ============================================================================
// PARTNER FORM MODAL — FSA Partners master (NCP-13/14)
// ============================================================================
// Create/Edit master form for fsa.tbl_partner. Client picker is async;
// effective_from/to carve the eligibility window. profit_sharing_ratio (%)
// auto-fills the Owners' Capital Account Note; is_signatory feeds the FS
// signature panel.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import notificationService from 'services/notificationService';
import { fsaPartnerService } from 'api/services/fsa/fsaPartnerService';
import { clientService } from 'api/services/clientService';
import type { Partner } from 'api/types/fsa/partner.types';

export interface PartnerFormData {
  clientId: string;
  partnerName: string;
  pan: string;
  designation: string;
  profitSharingRatio: string;
  isSignatory: boolean;
  signatureBlockText: string;
  displayOrder: string;
  effectiveFrom: string;
  effectiveTo: string;
}

const INITIAL_FORM_DATA: PartnerFormData = {
  clientId: '',
  partnerName: '',
  pan: '',
  designation: 'Partner',
  profitSharingRatio: '',
  isSignatory: true,
  signatureBlockText: '',
  displayOrder: '0',
  effectiveFrom: '',
  effectiveTo: '',
};

const DESIGNATION_OPTIONS = [
  { value: 'Partner', label: 'Partner' },
  { value: 'Managing Partner', label: 'Managing Partner' },
  { value: 'Designated Partner', label: 'Designated Partner' },
  { value: 'Whole-time Partner', label: 'Whole-time Partner' },
];

const toIsoDate = (s: string | null | undefined): string =>
  s ? new Date(s).toISOString().slice(0, 10) : '';

interface PartnerFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  record?: Partner | null;
  defaultClientId?: string;
  onSaved: () => void;
  onClose: () => void;
  // ---- Minimize-to-bar support ----
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit', instanceId?: string, recordId?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const PartnerFormModal: React.FC<PartnerFormModalProps> = ({
  isOpen, mode, record, defaultClientId, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<PartnerFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;
  const recordRef = useRef(record);
  recordRef.current = record;
  const modeRef = useRef(mode);
  modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId);
  createInstanceIdRef.current = createInstanceId;
  const initialSnapshotRef = useRef('');
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((field: string) => fieldErrors[field], [fieldErrors]);
  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) {
      isRestoreRef.current = false;
      setFieldErrors({});
      return;
    }
    const data: PartnerFormData = recordRef.current ? {
      clientId:           String(recordRef.current.clientId),
      partnerName:        recordRef.current.partnerName,
      pan:                recordRef.current.pan ?? '',
      designation:        recordRef.current.designation,
      profitSharingRatio: recordRef.current.profitSharingRatio != null ? String(recordRef.current.profitSharingRatio) : '',
      isSignatory:        recordRef.current.isSignatory,
      signatureBlockText: recordRef.current.signatureBlockText ?? '',
      displayOrder:       String(recordRef.current.displayOrder ?? 0),
      effectiveFrom:      toIsoDate(recordRef.current.effectiveFrom),
      effectiveTo:        toIsoDate(recordRef.current.effectiveTo),
    } : {
      ...INITIAL_FORM_DATA,
      clientId: defaultClientId ?? '',
    };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const loadClientOptions = useCallback(async (search: string) => {
    const resp = await clientService.getAll({ page: 1, pageSize: 50, searchTerm: search || undefined });
    return resp.data.map(c => ({
      value: String(c.id),
      label: c.legalName ? `${c.legalName} (${c.code})` : `${c.name} (${c.code})`,
    }));
  }, []);

  const seedOptions = useMemo(() => {
    if (record?.clientId && record?.clientLegalName) {
      return [{
        value: String(record.clientId),
        label: `${record.clientLegalName} (${record.clientCode ?? ''})`,
      }];
    }
    return undefined;
  }, [record]);

  const handleClose = useCallback(() => {
    isDirty ? setDiscardConfirmOpen(true) : onClose();
  }, [isDirty, onClose]);

  // --- Minimize handler (captures this session's data at minimize time) ---
  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecordId = recordRef.current ? String(recordRef.current.id) : undefined;

    const modalId = currentMode === 'create'
      ? `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`
      : `${tabId}-form-modal-edit-${currentRecordId || 'new'}`;

    let title: string;
    if (currentMode === 'create') {
      const name = currentFormData.partnerName.trim();
      const idx = currentCreateInstanceId || '1';
      title = name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.partnerName || 'Partner'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(savedFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId,
      title,
      moduleName,
      tabId,
      isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecordId : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!formData.clientId)             errors.clientId = 'Client is required';
    if (!formData.partnerName.trim())   errors.partnerName = 'Partner name is required';
    if (!formData.designation.trim())   errors.designation = 'Designation is required';
    if (!formData.effectiveFrom)        errors.effectiveFrom = 'Effective from date is required';
    if (formData.partnerName.length > 200) errors.partnerName = 'Name cannot exceed 200 characters';
    if (formData.pan && formData.pan.length > 20) errors.pan = 'PAN cannot exceed 20 characters';
    if (formData.profitSharingRatio !== '') {
      const psr = Number(formData.profitSharingRatio);
      if (Number.isNaN(psr) || psr < 0 || psr > 100) errors.profitSharingRatio = 'Share % must be between 0 and 100';
    }
    if (formData.effectiveTo && formData.effectiveFrom && formData.effectiveTo < formData.effectiveFrom) {
      errors.effectiveTo = 'Effective to must be on or after effective from';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const payload = {
        clientId:           formData.clientId,
        partnerName:        formData.partnerName.trim(),
        pan:                formData.pan.trim() || null,
        designation:        formData.designation.trim(),
        profitSharingRatio: formData.profitSharingRatio !== '' ? Number(formData.profitSharingRatio) : null,
        isSignatory:        formData.isSignatory,
        signatureBlockText: formData.signatureBlockText.trim() || null,
        displayOrder:       formData.displayOrder !== '' ? Number(formData.displayOrder) : 0,
        effectiveFrom:      formData.effectiveFrom,
        effectiveTo:        formData.effectiveTo || null,
        statusCode:         'ACTIVE',
      };
      if (mode === 'create') {
        await fsaPartnerService.create(payload);
      } else if (recordRef.current) {
        await fsaPartnerService.update(recordRef.current.id, {
          ...payload,
          rowVersion: recordRef.current.rowVersion,
        });
      }
      setIsSaving(false);
      notificationService.toast.success(`"${formData.partnerName}" ${mode === 'create' ? 'created' : 'updated'} successfully.`);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      notificationService.toast.error(err?.response?.data?.message ?? 'Save failed', 6000);
    }
  }, [mode, formData, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'create' ? 'Add Partner' : 'Edit Partner'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving} title="Close"><IconSystem.x size={20} /></button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="clientId">
            <SyutSelect
              label="Client (Firm)"
              required
              value={formData.clientId}
              onChange={(val) => { clearFieldError('clientId'); setFormData(prev => ({ ...prev, clientId: (val as string) || '' })); }}
              isAsync
              loadOptions={loadClientOptions}
              defaultOptions
              cacheOptions={false}
              seedOptions={seedOptions}
              error={getFieldError('clientId')}
              placeholder="Select partnership firm…"
            />
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="partnerName">
              <label className="form-label">Partner Name <span className="text-danger">*</span></label>
              <input
                type="text"
                className={`form-input${getFieldError('partnerName') ? ' form-field-error' : ''}`}
                value={formData.partnerName}
                onChange={(e) => { clearFieldError('partnerName'); setFormData(prev => ({ ...prev, partnerName: e.target.value })); }}
                maxLength={200}
              />
              {getFieldError('partnerName') && <div className="form-error-message">{getFieldError('partnerName')}</div>}
            </div>
            <div className="form-group" data-field="pan">
              <label className="form-label">PAN</label>
              <input
                type="text"
                className={`form-input${getFieldError('pan') ? ' form-field-error' : ''}`}
                value={formData.pan}
                onChange={(e) => { clearFieldError('pan'); setFormData(prev => ({ ...prev, pan: e.target.value.toUpperCase() })); }}
                placeholder="Optional"
                maxLength={20}
              />
              {getFieldError('pan') && <div className="form-error-message">{getFieldError('pan')}</div>}
            </div>
          </div>

          <div className="form-grid-3">
            <div className="form-group" data-field="designation">
              <SyutSelect
                label="Designation"
                required
                value={formData.designation}
                onChange={(val) => { clearFieldError('designation'); setFormData(prev => ({ ...prev, designation: (val as string) || 'Partner' })); }}
                options={DESIGNATION_OPTIONS}
                isClearable={false}
                error={getFieldError('designation')}
              />
            </div>
            <div className="form-group" data-field="profitSharingRatio">
              <label className="form-label">Profit Share %</label>
              <input
                type="number"
                step="0.0001"
                min={0}
                max={100}
                className={`form-input${getFieldError('profitSharingRatio') ? ' form-field-error' : ''}`}
                value={formData.profitSharingRatio}
                onChange={(e) => { clearFieldError('profitSharingRatio'); setFormData(prev => ({ ...prev, profitSharingRatio: e.target.value })); }}
                placeholder="e.g. 60"
              />
              {getFieldError('profitSharingRatio') && <div className="form-error-message">{getFieldError('profitSharingRatio')}</div>}
            </div>
            <div className="form-group" data-field="displayOrder">
              <label className="form-label">Display Order</label>
              <input
                type="number"
                step="1"
                min={0}
                className="form-input"
                value={formData.displayOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, displayOrder: e.target.value }))}
              />
            </div>
          </div>

          <div className="form-grid-2">
            <div className="form-group" data-field="effectiveFrom">
              <label className="form-label">Effective From <span className="text-danger">*</span></label>
              <input
                type="date"
                className={`form-input${getFieldError('effectiveFrom') ? ' form-field-error' : ''}`}
                value={formData.effectiveFrom}
                onChange={(e) => { clearFieldError('effectiveFrom'); setFormData(prev => ({ ...prev, effectiveFrom: e.target.value })); }}
              />
              {getFieldError('effectiveFrom') && <div className="form-error-message">{getFieldError('effectiveFrom')}</div>}
            </div>
            <div className="form-group" data-field="effectiveTo">
              <label className="form-label">Effective To</label>
              <input
                type="date"
                className={`form-input${getFieldError('effectiveTo') ? ' form-field-error' : ''}`}
                value={formData.effectiveTo}
                onChange={(e) => { clearFieldError('effectiveTo'); setFormData(prev => ({ ...prev, effectiveTo: e.target.value })); }}
              />
              {getFieldError('effectiveTo') && <div className="form-error-message">{getFieldError('effectiveTo')}</div>}
              <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                Empty = currently a partner.
              </div>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.isSignatory}
                onChange={(e) => setFormData(prev => ({ ...prev, isSignatory: e.target.checked }))}
              />
              Signs the financial statements
            </label>
          </div>

          <div className="form-group">
            <label className="form-label">Signature Block Text</label>
            <input
              type="text"
              className="form-input"
              value={formData.signatureBlockText}
              onChange={(e) => setFormData(prev => ({ ...prev, signatureBlockText: e.target.value }))}
              maxLength={500}
              placeholder="Optional per-partner line printed below their name"
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>Save</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Save"
        message={`${mode === 'create' ? 'Create' : 'Update'} partner "${formData.partnerName}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default PartnerFormModal;
